import { COLOR_VARIANT } from 'models/Colors';
import { getLatestDate, isEarlierDate, wasDateWithinLastDay } from 'dateUtils';
import { getHasElementsInArray } from 'utils';
import {
  DATA_ROOM_RFI_CREATION_TYPE,
  DATA_ROOM_RFI_DISPLAY_STATUS,
  DataRoomRFI,
  DataRoomRFICreationType,
  DataRoomRFIDisplayStatus,
  DataRoomRFIStatusCounts,
  DataRoomRfiRecipient,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { EMPTY_ARRAY } from 'data/global';

export const RFI_DISPLAY_STATUS_LABEL_KEYS: Record<string, string> = {
  [DATA_ROOM_RFI_DISPLAY_STATUS.NOT_STARTED]: 'notStarted',
  [DATA_ROOM_RFI_DISPLAY_STATUS.PARTIALLY_COMPLETED]: 'partiallyCompleted',
  [DATA_ROOM_RFI_DISPLAY_STATUS.COMPLETED]: 'completed',
  [DATA_ROOM_RFI_DISPLAY_STATUS.CLOSED]: 'closed',
};

export const RFI_DISPLAY_STATUS_COLOR: Record<string, string> = {
  [DATA_ROOM_RFI_DISPLAY_STATUS.NOT_STARTED]: COLOR_VARIANT.GREY,
  [DATA_ROOM_RFI_DISPLAY_STATUS.PARTIALLY_COMPLETED]: COLOR_VARIANT.ORANGE,
  [DATA_ROOM_RFI_DISPLAY_STATUS.COMPLETED]: COLOR_VARIANT.GREEN,
  [DATA_ROOM_RFI_DISPLAY_STATUS.CLOSED]: COLOR_VARIANT.BLUE,
};

const DAY_IN_MS = 1000 * 60 * 60 * 24;

export const getDayDiff = (date: Date | string) => {
  const today = new Date();
  const target = new Date(date);

  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startOfTarget = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate(),
  );

  return Math.round(
    (startOfTarget.getTime() - startOfToday.getTime()) / DAY_IN_MS,
  );
};

export const getIsRfiDisplayStatusClosed = (
  displayStatus: DataRoomRFIDisplayStatus,
) => displayStatus === DATA_ROOM_RFI_DISPLAY_STATUS.CLOSED;

export const getIsRfiDisplayStatusCompleted = (
  displayStatus: DataRoomRFIDisplayStatus,
) => displayStatus === DATA_ROOM_RFI_DISPLAY_STATUS.COMPLETED;

export const getIsRfiDisplayStatusNotStarted = (
  displayStatus: DataRoomRFIDisplayStatus,
) => displayStatus === DATA_ROOM_RFI_DISPLAY_STATUS.NOT_STARTED;

export const getHasRfiNewSubmission = (rfi: DataRoomRFI) => {
  const { displayStatus, lastOpenedAt, lastSubmittedAt } = rfi;

  return (
    !getIsRfiDisplayStatusNotStarted(displayStatus) &&
    isEarlierDate(lastOpenedAt, lastSubmittedAt)
  );
};

export const getIsRfiCreationTypeChecklist = (
  creationType: DataRoomRFICreationType,
) => creationType === DATA_ROOM_RFI_CREATION_TYPE.CHECKLIST;

export const getIsRfiCreationTypeSpreadsheet = (
  creationType: DataRoomRFICreationType,
) => creationType === DATA_ROOM_RFI_CREATION_TYPE.SPREADSHEET;

export const getRfiDisplayStatusAfterReopen = (rfi: DataRoomRFI) => {
  const { completedCategories, totalCategories } = rfi;

  if (totalCategories && completedCategories === totalCategories) {
    return DATA_ROOM_RFI_DISPLAY_STATUS.COMPLETED;
  }

  if (!completedCategories) {
    return DATA_ROOM_RFI_DISPLAY_STATUS.NOT_STARTED;
  }

  return DATA_ROOM_RFI_DISPLAY_STATUS.PARTIALLY_COMPLETED;
};

export const getIsRfiOverdue = (rfi: DataRoomRFI) => {
  const { dueDate } = rfi || {};

  if (!dueDate) return false;

  return getDayDiff(dueDate) < 0;
};

export const getIsRecipientRemindedRecently = ({
  reminders,
}: DataRoomRfiRecipient) =>
  getHasElementsInArray(reminders) &&
  wasDateWithinLastDay(getLatestDate(reminders));

export const getOutstandingReminderRecipients = ({
  allRecipientsWithReminders,
}: DataRoomRFI) =>
  (allRecipientsWithReminders || EMPTY_ARRAY).filter(
    (recipient) => !getIsRecipientRemindedRecently(recipient),
  );

export const getRecentlyRemindedRecipients = ({
  allRecipientsWithReminders,
}: DataRoomRFI) =>
  (allRecipientsWithReminders || EMPTY_ARRAY).filter(
    getIsRecipientRemindedRecently,
  );

export const RFI_STATUS_COUNT_SUMMARY_KEYS: Record<string, string> = {
  [DATA_ROOM_RFI_DISPLAY_STATUS.NOT_STARTED]: 'rfiNotStartedCountSummary',
  [DATA_ROOM_RFI_DISPLAY_STATUS.PARTIALLY_COMPLETED]:
    'rfiPartiallyCompletedCountSummary',
  [DATA_ROOM_RFI_DISPLAY_STATUS.COMPLETED]: 'rfiCompletedCountSummary',
  [DATA_ROOM_RFI_DISPLAY_STATUS.CLOSED]: 'rfiClosedCountSummary',
};

export const getRfiStatusCountsSummaryProps = (
  statusFilter: string,
  statusCounts: DataRoomRFIStatusCounts,
): [string, Record<string, number>] =>
  RFI_STATUS_COUNT_SUMMARY_KEYS[statusFilter]
    ? [
        RFI_STATUS_COUNT_SUMMARY_KEYS[statusFilter],
        {
          count:
            statusCounts[
              RFI_DISPLAY_STATUS_LABEL_KEYS[
                statusFilter
              ] as keyof DataRoomRFIStatusCounts
            ],
        },
      ]
    : ['rfiStatusCountsSummary', statusCounts];
